import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { LANGUAGES, PARTNER_ERROR_CODES, TEMPLATE_CONSTANTS_IDS } from '@constants/app.constants';
import { SSR_RESPONSE_CONTEXT } from '@constants/ssr-response.token';
import { CssOverwritesDbModel } from '@data-models/css-overwrites.model';
import { IPartnerInformationBody, PartnerDbModel } from '@data-models/partner-db.model';
import { CustomBookingMessageTemplateDbModel } from '@data-models/templates-db.model';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { PartnerService } from '@services/feature-service/partner.service';
import { Observable, catchError, forkJoin, of, switchMap } from 'rxjs';
import { HelperService } from '../helper/helper.service';
import { DomManipulationService } from '../util-service/dom-manipulation.service';

/**
 * Title a partner page falls back to when the template call yields nothing.
 *
 * Mirrors the shape WHITELABEL_BOOKINGPAGE_PAGETITLE produces, which is now the partner
 * name alone for every partner. It used to carry the brand as a prefix, which meant ~11,900
 * titles opened on the same token — formulaic enough that Google discarded them and wrote its
 * own, taking the replacement from a neighbouring booking page. Any partner-derived title
 * beats the shell default, which is byte-identical across every URL on the domain.
 *
 * The brand survives only where there is no name to show at all, since an empty title is worse
 * than a generic one. Keeping this in step with the template matters: the two disagreeing is
 * exactly the drift that produced the original wrong-title bug.
 */
function buildFallbackTitle(partnerName: string | undefined): string {
  return partnerName?.trim() || environment.brandName;
}

/**
 * Describes the partner as a business, in the vocabulary search engines read.
 *
 * Only facts go in here — who they are, where they are, how to reach them. Anything a partner
 * wrote as prose is deliberately excluded: that text was authored to be read on the page, and
 * republishing it as machine-readable metadata is a decision about how ~12,000 businesses appear
 * in search, not an implementation detail.
 *
 * A field the backend withheld — it redacts address, phone and email according to each partner's
 * own hide flags — simply does not appear here. Absent means omitted, never guessed or defaulted:
 * the brand's placeholder logo, for instance, is not this business's image.
 *
 * The website is the exception and has to be checked here: `hide_website_on_bookingsite` is
 * honoured in the templates, not in redactInformationResponse(), so `url` arrives populated even
 * when the partner has hidden it.
 */
function buildLocalBusiness(partner: PartnerDbModel, pageUrl: string): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    // Anchored to the page so the same business stays one entity across its several URL shapes.
    '@id': `${pageUrl}#business`,
    name: partner?.name,
    url: pageUrl,
  };

  const street = partner?.street?.trim();
  const zip = partner?.zip?.trim();
  const city = partner?.city?.trim();
  if (street || zip || city) {
    const address: Record<string, unknown> = { '@type': 'PostalAddress' };
    street && (address['streetAddress'] = street);
    zip && (address['postalCode'] = zip);
    city && (address['addressLocality'] = city);
    partner?.country?.identifier && (address['addressCountry'] = partner.country.identifier.toUpperCase());
    payload['address'] = address;
  }

  partner?.phone?.trim() && (payload['telephone'] = partner.phone.trim());
  partner?.email?.trim() && (payload['email'] = partner.email.trim());
  partner?.logo && (payload['image'] = `${environment.partnersImagePath}${partner.uuid}/${partner.logo}`);

  // Gated on the partner's own hide flag, which the backend does not apply for this field: the
  // page suppresses the visible link, so publishing the same URL as structured data on that page
  // would hand back exactly what the partner asked us to withhold. An unset flag reads as hidden,
  // matching how the templates test it.
  //
  // Partners store their website without a scheme ("www.example.com"), which is not a usable
  // identifier here — sameAs must be an absolute URL or it is worthless as an identity claim.
  // Same test HelperService.openWebsite() uses, but https: a business identity should not be
  // published over plaintext.
  const website = Number(partner?.hide_website_on_bookingsite) === 0 ? partner?.url?.trim() : '';
  if (website) {
    payload['sameAs'] = [/^https?:\/\//i.test(website) ? website : `https://${website}`];
  }

  return payload;
}

export const PartnerResolveFn: ResolveFn<PartnerDbModel> = (
  route: ActivatedRouteSnapshot, state: RouterStateSnapshot
) => {
  let _partnerData = new PartnerDbModel();
  const partnerService = inject(PartnerService);
  const domManipulationService = inject(DomManipulationService);
  const helperService = inject(HelperService);
  const translate = inject(TranslateService);
  const transferState = inject(TransferState);
  const platformId = inject(PLATFORM_ID);
  // Only provided during SSR — absent (and irrelevant) on the client.
  const ssrResponseContext = inject(SSR_RESPONSE_CONTEXT, { optional: true });
  const templateConstantsIds = TEMPLATE_CONSTANTS_IDS;

  let observables: {
    templates: Observable<CustomBookingMessageTemplateDbModel[]>,
    cssOverwrites: Observable<CssOverwritesDbModel | null>
  } = {
    templates: partnerService.getPartnerTemplates(),
    cssOverwrites: of(null)
  };

  // Build effective query params: merge decoded q params with direct query params
  let effectiveParams = { ...route.queryParams };
  if (route.queryParams?.q) {
    try {
      const decodedParams = helperService.decodeBase64QueryParams(route.queryParams.q);
      for (const key of Object.keys(decodedParams)) {
        if (effectiveParams[key] === undefined) {
          effectiveParams[key] = decodedParams[key];
        }
      }
    } catch (e) {
      // Invalid base64, ignore
    }
  }

  const partnerBody: IPartnerInformationBody = {
    booking_name: (route.params?.bookingName ? route.params.bookingName : environment.defaultBookingPartnerName)
  };
  const language = (effectiveParams?.lang || effectiveParams?.language);

  route.params?.workerUsername && (partnerBody.username = route.params.workerUsername?.toLowerCase());

  if (effectiveParams?.internal === 'true' && effectiveParams?.token) {
    partnerBody.internal = true;
    partnerBody.token = effectiveParams.token;
  }

  if (effectiveParams?.worker_id && !isNaN(effectiveParams?.worker_id)) {
    partnerBody.worker_id = Number(effectiveParams?.worker_id);
  }

  const storeIdParam = effectiveParams?.store_id ?? effectiveParams?.['store-id'];
  if (storeIdParam && !isNaN(Number(storeIdParam))) {
    partnerBody.store_id = Number(storeIdParam);
  }

  // Use TransferState to prevent SSR hydration flash: on client, reuse data resolved during SSR
  const stateKey = makeStateKey<PartnerDbModel>('partner-' + partnerBody.booking_name);
  if (isPlatformBrowser(platformId) && transferState.hasKey(stateKey)) {
    const cachedPartner = transferState.get(stateKey, null);
    transferState.remove(stateKey);
    if (cachedPartner?.uuid) {
      _partnerData = Object.assign(new PartnerDbModel(), cachedPartner);
      partnerService.restoreFromCache(_partnerData, partnerBody.internal === true);
    }
  }

  // If not restored from cache, fetch partner data from API
  const partnerData$ = _partnerData?.uuid
    ? of(_partnerData)
    : partnerService.getPartnersWidgetInformation(partnerBody, false);

  return partnerData$.pipe(
    switchMap((partner: PartnerDbModel) => {
      _partnerData = partner;
      const selectedLang = helperService.getDefaultLanguage(language);
      translate.use(selectedLang);
      translate.setFallbackLang(selectedLang);

      const pageUrl = `${environment.deployUrl}${state.url}`;
      domManipulationService.setOgURL(pageUrl);
      domManipulationService.setDocumentLanguage(selectedLang);
      // `supported_widget_languages` holds short codes ('de', 'fr'); LANGUAGES is the same
      // list the language switcher renders from, so the alternates can only name languages
      // a visitor could actually have picked.
      domManipulationService.setLanguageAlternates(
        pageUrl,
        (_partnerData?.supported_widget_languages || [])
          .map(shortCode => LANGUAGES.find(supported => supported.value === shortCode)?.locale)
          .filter((locale): locale is string => !!locale)
      );
      _partnerData?.landingpage_header_background_color && (domManipulationService.setThemeColor(_partnerData.landingpage_header_background_color));

      // Lay down partner-derived title + description NOW, before the template call is
      // attempted. getPartnerTemplates() swallows its own failures and resolves to [],
      // and every piece of SEO metadata used to hang off that array — so a single
      // transient API blip shipped the page with the shell's "Calenso Booking" title,
      // byte-identical to every other URL on the domain. Google treats a site-wide
      // boilerplate title as non-descriptive and substitutes one of its own, drawn from
      // whatever else it associates with the page — which is how one partner's booking
      // page ended up titled with a different Calenso customer's company name.
      // The template values still win when they arrive; these only guarantee a floor.
      domManipulationService.setDocumentTitleText(
        buildFallbackTitle(_partnerData?.name)
      );

      const fallbackDescriptionKey = 'general.booking_page_meta_description';
      const fallbackDescription = translate.instant(fallbackDescriptionKey, {
        partner: _partnerData?.name?.trim() || environment.brandName
      });
      // instant() echoes the key back when it can't resolve it — never ship
      // "general.booking_page_meta_description" as a meta description.
      if (fallbackDescription && fallbackDescription !== fallbackDescriptionKey) {
        domManipulationService.setDocumentDescriptionText(fallbackDescription);
      }

      // A partner can ask to stay out of search results. Every route for their booking page runs
      // through this resolver, so honouring it once here covers the worker, service, events and
      // conference URLs as well as the page itself.
      //
      // Removing the structured data rather than merely not adding it: on a client-side
      // navigation the document carries over, so a block written for the previous page would
      // otherwise survive onto one that is asking not to be listed.
      // A /<partner>/<username> URL without a worker renders the partner page; keyed on the missing
      // worker, not a backend flag, so the response never reveals whether a username exists.
      const workerSegment = route.params?.workerUsername?.toLowerCase();
      const isPersonalPageWithoutWorker =
        !!workerSegment &&
        !['services', 'events'].includes(workerSegment) &&
        !_partnerData?.worker;

      if (Number(_partnerData?.booking_page_hide_from_search_engines) === 1 || isPersonalPageWithoutWorker) {
        domManipulationService.setRobotsNoIndex();
        domManipulationService.removeLocalBusinessJsonLd();
        if (ssrResponseContext) {
          ssrResponseContext.noIndex = true;
        }
      } else {
        domManipulationService.setLocalBusinessJsonLd(buildLocalBusiness(_partnerData, pageUrl));
      }

      if (!partnerService.isWhitelabeledPartner) {
        domManipulationService.setAuthorMeta(environment.brandName, environment.brandName);
        domManipulationService.setDocumentFavicon(`${environment.deployUrl}/images/brands/${environment.brandFolder}/favicon.ico`);
        if (!state.url.includes('events')) {
          domManipulationService.setOgImageMeta(
            `${environment.deployUrl}/images/brands/${environment.brandFolder}/placeholder.png`,
            environment.brandName
          );
        }
      } else {
        domManipulationService.setAuthorMeta(_partnerData?.name, _partnerData?.name);
        if (_partnerData?.favicon_logo) {
          domManipulationService.setDocumentFavicon(`${environment.faviconImagePath}${_partnerData.uuid}/${_partnerData?.favicon_logo}`);
        } else {
          domManipulationService.setDocumentFavicon(`${environment.deployUrl}/images/brands/${environment.brandFolder}/favicon.ico`);
        }

        if (!state.url.includes('events')) {
          if (_partnerData?.landingpage_logo) {
            domManipulationService.setOgImageMeta(
              `${environment.landingPageImagePath}${_partnerData.uuid}/${_partnerData?.landingpage_logo}`,
              _partnerData?.name
            );
          } else {
            // No partner landing-page logo → fall back to the brand image instead of a broken
            // `.../landingpage_logo/<uuid>/null` URL (which 404s and kills the link-preview card).
            domManipulationService.setOgImageMeta(
              `${environment.deployUrl}/images/brands/${environment.brandFolder}/placeholder.png`,
              _partnerData?.name
            );
          }
        }

        // Check for cusom css overwrite if is_custom_css_bookingpage_enabled is enabled and partner is whitelabled
        if (Number(partner?.is_custom_css_bookingpage_enabled) === 1) {
          observables.cssOverwrites = partnerService.getCssOverwrites();
        }
      }

      return forkJoin(observables).pipe(
        switchMap(result => {
          const metaTemplates: CustomBookingMessageTemplateDbModel[] = [];

          if (result?.templates?.length) {
            result.templates.filter(template => {
              return [
                templateConstantsIds.WHITELABEL.WHITELABEL_BOOKINGPAGE_PAGETITLE,
                templateConstantsIds.WHITELABEL.WHITELABEL_BOOKINGPAGE_DESCRIPTION_APPOINTMENT,
                templateConstantsIds.WHITELABEL.WHITELABEL_BOOKINGPAGE_DESCRIPTION_EVENT
              ].includes(template.id);
            }).forEach(template => {
              template.is_multi_language = 1;
              metaTemplates[template.id] = template;
            });

            if (metaTemplates[templateConstantsIds.WHITELABEL.WHITELABEL_BOOKINGPAGE_PAGETITLE] !== undefined) {
              domManipulationService.setDocumentTitle(metaTemplates[templateConstantsIds.WHITELABEL.WHITELABEL_BOOKINGPAGE_PAGETITLE], _partnerData?.name);
            }

            if (state.url.includes('events')) {
              if (metaTemplates[templateConstantsIds.WHITELABEL.WHITELABEL_BOOKINGPAGE_DESCRIPTION_EVENT] !== undefined) {
                domManipulationService.setDocumentDescription(metaTemplates[templateConstantsIds.WHITELABEL.WHITELABEL_BOOKINGPAGE_DESCRIPTION_EVENT], _partnerData?.name);
              }
            } else {
              if (metaTemplates[templateConstantsIds.WHITELABEL.WHITELABEL_BOOKINGPAGE_DESCRIPTION_APPOINTMENT] !== undefined) {
                domManipulationService.setDocumentDescription(metaTemplates[templateConstantsIds.WHITELABEL.WHITELABEL_BOOKINGPAGE_DESCRIPTION_APPOINTMENT], _partnerData?.name);
              }
            }
          }

          // Apply CSS overwrites if set by partner
          result?.cssOverwrites && partnerService.partnerCssOverwritesEvent.emit(result.cssOverwrites);

          // Store resolved partner data in TransferState for client hydration
          if (isPlatformServer(platformId)) {
            transferState.set(stateKey, _partnerData);
          }

          partnerService.partnerLoadEvent.emit(_partnerData);
          return of(_partnerData);
        }),
        catchError(() => {
          partnerService.partnerLoadEvent.emit(_partnerData);
          return of(_partnerData);
        }),
      );
    }),
    catchError(error => {
      const selectedLang = helperService.getDefaultLanguage(language);
      translate.use(selectedLang);
      translate.setFallbackLang(selectedLang);
      // The error pages (404, "booking unavailable", "try again later") are rendered in
      // this language too, so the document must still say so. No alternates here — we have
      // no partner, so we do not know which languages this URL is served in.
      domManipulationService.setDocumentLanguage(selectedLang);

      // A blocked or deleted partner is ALSO refused with 403, so the status
      // alone cannot distinguish it from a missing subscription — without the
      // error_code check a blocked account renders "Missing subscription",
      // which sends the visitor down the wrong support path.
      const errorCode = error?.error?.errors?.[0]?.error_code;
      const errorMessage = error?.error?.errors?.[0]?.message;

      // Every /api/v1 failure leaves our backend through one renderer, so a real verdict
      // about this partner ALWAYS arrives with an errors[] envelope. The infrastructure in
      // front of it does not: Myra's WAF answers a rate-limited burst with a bare 403, and
      // the gateway answers an overloaded upstream with a bare 502. Requiring the envelope
      // is what keeps those from being read as statements about the partner — observed on
      // DEV, where a load burst produced 7 bare 403s on a perfectly healthy account.
      const hasApiErrorEnvelope = Array.isArray(error?.error?.errors) && error.error.errors.length > 0;

      // "No such booking name" is concluded from the MESSAGE, not the status: that response
      // carries no error_code, and CakePHP returns a bare 404 for a wrong route/method too.
      const isPartnerNotFound =
        hasApiErrorEnvelope &&
        error.status === PARTNER_ERROR_CODES.PARTNER_NOT_FOUND &&
        errorMessage === PARTNER_ERROR_CODES.PARTNER_NOT_FOUND_MESSAGE;

      // ALLOWLIST, deliberately — only a response that NAMES its reason is a verdict about
      // this partner. Everything else is infrastructure and must never touch indexing.
      // Written as an allowlist rather than a list of transient cases because the failure
      // that got through the previous version was one nobody thought to enumerate.
      //
      // A bare 403 is deliberately NOT on this list. The only ForbiddenExceptions reachable
      // from partners/information are the two that carry an error_code (blocked, booking
      // disabled) and CalensoAuthHelper's "Something went wrong when creating the widget
      // token." — which is an upstream auth-service failure, not a statement about the
      // partner. There is no genuine no-subscription 403 on this endpoint, so treating an
      // unnamed 403 as one told visitors of a healthy account "Missing subscription" and
      // noindex'd their page. Observed intermittently on DEV under concurrent load.
      const isAuthoritativeGateResponse =
        hasApiErrorEnvelope && (
          errorCode === PARTNER_ERROR_CODES.PARTNER_BLOCKED ||
          errorCode === PARTNER_ERROR_CODES.BOOKING_WIDGET_DISABLED ||
          isPartnerNotFound
        );

      const isTransientFailure = !isAuthoritativeGateResponse;

      _partnerData.is_blocked = errorCode === PARTNER_ERROR_CODES.PARTNER_BLOCKED;
      // A partner whose booking widget is switched off (JB-1637) also answers 403;
      // flag it distinctly so it renders "booking unavailable" instead of the
      // wrong "Missing subscription" path (same reasoning as is_blocked above).
      _partnerData.is_booking_widget_disabled = errorCode === PARTNER_ERROR_CODES.BOOKING_WIDGET_DISABLED;
      // A transient failure is not a gate response — render "something went wrong, try
      // again later" rather than the misleading "invalid partner address" the !uuid
      // fallback would show. A genuine bad address is deliberately left to that fallback.
      _partnerData.is_bootstrap_error = isTransientFailure;
      // Left exactly as it was. The widget-token 403 would make this false, but it never
      // gets read: is_bootstrap_error is now true for that case and app.component checks
      // it FIRST, so the visitor sees "something went wrong" rather than the old, wrong
      // "Missing subscription". Guarding this expression as well would only make it a
      // tautology and quietly delete the one path a genuine subscription-lapse 403 would
      // ever arrive on.
      _partnerData.has_active_subscription =
        _partnerData.is_blocked ||
        _partnerData.is_booking_widget_disabled ||
        error.status !== PARTNER_ERROR_CODES.NOT_ACTIVE_SUBSCRIPTION;

      // Nothing bookable lives at this URL, so keep it out of the index — these pages
      // all render the same shell copy under the same shell title, and indexing them is
      // what builds the pool of identical-title pages Google pulls replacement titles
      // from. A definitive "no such booking name" additionally answers a real 404
      // instead of 200 + an indexable shell.
      //
      // A transient failure gets NEITHER — it gets 503. This is the case that made the
      // original bug survive a first fix: when the partner call itself fails, the
      // resolver's success branch never runs, so the partner-derived title never lands
      // and the page ships the shell's boilerplate title. Answering 200 there invites
      // crawlers to index that boilerplate; answering 503 tells them to retry and keep
      // the entry they already have. Measured at ~1 render in 8 on DEV under concurrent
      // load, so this is the COMMON failure, not the rare one.
      if (isTransientFailure) {
        if (ssrResponseContext) {
          ssrResponseContext.status = PARTNER_ERROR_CODES.SERVICE_UNAVAILABLE;
        }
      } else {
        domManipulationService.setRobotsNoIndex();
        // Alternates travel with indexability: a page we have just declared noindex must
        // not keep advertising language variants of itself. On a server render there are
        // none to remove; this matters after a client-side navigation, where the document
        // carries over and would otherwise keep the previous page's alternates. Paired
        // with setRobotsNoIndex rather than applied to the whole catch block on purpose —
        // a transient failure keeps what it has (see above).
        domManipulationService.clearLanguageAlternates();
        if (isPartnerNotFound && ssrResponseContext) {
          ssrResponseContext.status = PARTNER_ERROR_CODES.PARTNER_NOT_FOUND;
        }
      }

      partnerService.partnerLoadEvent.emit(_partnerData);
      return of(_partnerData);
    }),
  );
}
